/**
 * Assign Bases Management Page
 * Full CRUD interface for managing assign_bases table
 */

import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Edit,
  Eye,
  Plus,
  RefreshCw,
  Save,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useToast } from '../../hooks/use-toast';
import {
  AssignBases,
  CreateAssignBasesInput,
  TablesToLoaded
} from '../../types/assignBases.types';
import { supabase } from '../../utils/supabaseClient';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const AssignBasesPage: React.FC = () => {
  const { toast } = useToast();

  // State
  const [assignments, setAssignments] = useState<AssignBases[]>([]);
  const [filteredAssignments, setFilteredAssignments] = useState<AssignBases[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTenantFilter, setSelectedTenantFilter] = useState<string>('all');

  // Spaces/Organizations state (tenants with space_id)
  const [spaces, setSpaces] = useState<Array<{ tenant_id: string; tenant_name: string; space_id: string | null }>>([]);
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false);

  // Available bases from sys_seed_config with table info
  type TableConfig = {
    tablename: string; // Table name field
    tableName: string; // From table_json.name
    tableId?: string; // For future use when tables exist
  };

  type BaseGroup = {
    baseName: string; // Base name (name field)
    tables: TableConfig[];
  };

  const [availableBases, setAvailableBases] = useState<BaseGroup[]>([]);
  const [isLoadingBases, setIsLoadingBases] = useState(true);
  const [expandedBases, setExpandedBases] = useState<Set<string>>(new Set());

  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<AssignBases | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateAssignBasesInput>({
    type: 'onboarding', // Default type
    bases_to_loaded: [],
    tables_to_loaded: {},
    tenant_ids: null, // Default to global (all organizations)
  });

  const [isSaving, setIsSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [baseSearchQuery, setBaseSearchQuery] = useState('');

  // Toggle base selection
  const toggleBaseSelection = (base: string) => {
    const currentBases = formData.bases_to_loaded || [];
    if (currentBases.includes(base)) {
      // Remove base and all its tables
      const newTablesToLoaded = { ...formData.tables_to_loaded };
      delete newTablesToLoaded[base];
      setFormData({
        ...formData,
        bases_to_loaded: currentBases.filter((b) => b !== base),
        tables_to_loaded: newTablesToLoaded,
      });
    } else {
      setFormData({
        ...formData,
        bases_to_loaded: [...currentBases, base],
      });
    }
  };

  // Toggle table selection within a base
  // Note: tablename parameter is the actual tablename field from sys_seed_config, not the display name
  const toggleTableSelection = (baseName: string, tablename: string) => {
    const currentTables = formData.tables_to_loaded || {};
    const baseTables = currentTables[baseName] || [];

    let newBaseTables: string[];
    if (baseTables.includes(tablename)) {
      newBaseTables = baseTables.filter((t) => t !== tablename);
    } else {
      newBaseTables = [...baseTables, tablename];
    }

    const newTablesToLoaded = { ...currentTables };
    if (newBaseTables.length > 0) {
      newTablesToLoaded[baseName] = newBaseTables;
    } else {
      delete newTablesToLoaded[baseName];
    }

    setFormData({
      ...formData,
      tables_to_loaded: newTablesToLoaded,
    });
  };

  // Toggle base expansion
  const toggleBaseExpansion = (baseName: string) => {
    const newExpanded = new Set(expandedBases);
    if (newExpanded.has(baseName)) {
      newExpanded.delete(baseName);
    } else {
      newExpanded.add(baseName);
    }
    setExpandedBases(newExpanded);
  };

  // Check if table is selected
  // Note: tablename parameter is the actual tablename field from sys_seed_config, not the display name
  const isTableSelected = (baseName: string, tablename: string): boolean => {
    const baseTables = formData.tables_to_loaded?.[baseName];
    return baseTables ? baseTables.includes(tablename) : false;
  };

  // Check if all tables in base are selected
  const areAllTablesSelected = (baseName: string): boolean => {
    const baseGroup = availableBases.find((b) => b.baseName === baseName);
    if (!baseGroup || baseGroup.tables.length === 0) return false;

    const baseTables = formData.tables_to_loaded?.[baseName] || [];
    // If no tables selected, return false
    // If tables selected equals available tables, return true
    // Note: We check against tablename (not tableName) since that's what we store
    return baseTables.length > 0 && baseTables.length === baseGroup.tables.length &&
      baseGroup.tables.every((table) => baseTables.includes(table.tablename));
  };

  // Load available bases from sys_seed_config with table information
  const loadAvailableBases = async () => {
    setIsLoadingBases(true);
    try {
      const { data, error } = await supabase
        .schema('altzero')
        .from('sys_seed_config')
        .select('tablename, name, table_json')
        .eq('status', 'active');

      if (error) throw error;

      // Group by base name (name field) and collect all tables
      const baseMap = new Map<string, TableConfig[]>();

      data.forEach((item) => {
        const baseName = item.name; // Base name
        const tablename = item.tablename; // Table name field

        // Extract table display name from table_json
        let tableJson = item.table_json;
        if (typeof tableJson === 'string') {
          try {
            tableJson = JSON.parse(tableJson);
          } catch (e) {
            console.warn(`Failed to parse table_json for ${baseName}/${tablename}:`, e);
            tableJson = {};
          }
        }
        const tableName = (tableJson as any)?.name || tablename;

        if (!baseMap.has(baseName)) {
          baseMap.set(baseName, []);
        }

        baseMap.get(baseName)!.push({
          tablename,
          tableName,
        });
      });

      // Convert to array and sort
      const basesArray: BaseGroup[] = Array.from(baseMap.entries())
        .map(([baseName, tables]) => ({
          baseName,
          tables: tables.sort((a, b) => a.tableName.localeCompare(b.tableName)),
        }))
        .sort((a, b) => a.baseName.localeCompare(b.baseName));

      setAvailableBases(basesArray);
    } catch (error) {
      console.error('Error loading available bases:', error);
      toast({
        title: 'Error',
        description: 'Failed to load available bases',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingBases(false);
    }
  };

  // Load spaces (tenants with space_id from Teable)
  const loadOrganizations = async () => {
    setIsLoadingOrganizations(true);
    try {
      // Query all tenants that have a space_id (Teable spaces)
      const { data, error } = await supabase
        .schema('altzero')
        .from('base_tenants')
        .select('tenant_id, tenant_name, space_id')
        .not('space_id', 'is', null)
        .eq('is_active', true)
        .order('tenant_name', { ascending: true });

      if (error) throw error;

      setSpaces(data || []);
    } catch (error) {
      console.error('Error loading spaces:', error);
      toast({
        title: 'Error',
        description: 'Failed to load spaces from Teable',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingOrganizations(false);
    }
  };

  // Load assignments
  const loadAssignments = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .schema('altzero')
        .from('assign_bases')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform data to include organization names and isGlobal flag
      const transformedData = (data || []).map((item: any) => {
        // Map tenant_ids to names if available
        // Note: We need to fetch tenant names separately or load all spaces first (which we do)
        // For now, we'll rely on the 'spaces' state which should be loaded
        return {
          ...item,
          isGlobal: !item.tenant_ids || item.tenant_ids.length === 0,
        };
      });

      setAssignments(transformedData);
      setFilteredAssignments(transformedData);
    } catch (error) {
      console.error('Error loading assignments:', error);
      toast({
        title: 'Error',
        description: 'Failed to load assignments',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    loadAvailableBases();
    loadOrganizations();
    loadAssignments();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...assignments];

    // Filter by organization
    if (selectedTenantFilter !== 'all') {
      if (selectedTenantFilter === 'global') {
        filtered = filtered.filter((assignment) => !assignment.tenant_ids || assignment.tenant_ids.length === 0);
      } else {
        filtered = filtered.filter((assignment) => assignment.tenant_ids?.includes(selectedTenantFilter));
      }
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(
        (assignment) =>
          assignment.type.toLowerCase().includes(searchQuery.toLowerCase()) ||
          assignment.bases_to_loaded.some((base) =>
            base.toLowerCase().includes(searchQuery.toLowerCase())
          ) ||
          (assignment.organization_name?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false)
      );
    }

    setFilteredAssignments(filtered);
  }, [assignments, searchQuery, selectedTenantFilter]);

  const handleCreate = () => {
    setFormData({
      type: 'onboarding',
      bases_to_loaded: [],
      tables_to_loaded: {},
      tenant_ids: null, // Default to global
    });
    setDialogError(null);
    setBaseSearchQuery('');
    setExpandedBases(new Set());
    setIsCreateDialogOpen(true);
  };

  const handleEdit = (assignment: AssignBases) => {
    setSelectedAssignment(assignment);
    setFormData({
      type: assignment.type,
      bases_to_loaded: [...assignment.bases_to_loaded],
      tables_to_loaded: assignment.tables_to_loaded || {},
      tenant_ids: assignment.tenant_ids || null,
    });
    setDialogError(null);
    setBaseSearchQuery('');
    // Expand bases that have table selections
    const basesWithTables = new Set(
      Object.keys(assignment.tables_to_loaded || {})
    );
    setExpandedBases(basesWithTables);
    setIsEditDialogOpen(true);
  };

  const handleView = (assignment: AssignBases) => {
    setSelectedAssignment(assignment);
    setIsViewDialogOpen(true);
  };

  const handleDelete = (assignment: AssignBases) => {
    setSelectedAssignment(assignment);
    setIsDeleteDialogOpen(true);
  };

  const handleSaveCreate = async () => {
    if (!formData.type.trim()) {
      setDialogError('Type is required');
      return;
    }

    setIsSaving(true);
    setDialogError(null);
    try {
      // Clean up tables_to_loaded: remove entries for bases not in bases_to_loaded
      const cleanedTablesToLoaded: TablesToLoaded = {};
      (formData.bases_to_loaded || []).forEach((baseName) => {
        if (formData.tables_to_loaded?.[baseName]) {
          cleanedTablesToLoaded[baseName] = formData.tables_to_loaded[baseName];
        }
      });

      const { data, error } = await supabase
        .schema('altzero')
        .from('assign_bases')
        .insert({
          type: formData.type.trim(),
          bases_to_loaded: formData.bases_to_loaded || [],
          tables_to_loaded: Object.keys(cleanedTablesToLoaded).length > 0
            ? cleanedTablesToLoaded
            : null,
          tenant_ids: formData.tenant_ids && formData.tenant_ids.length > 0 ? formData.tenant_ids : null,
        })
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          throw new Error(`An assignment with type "${formData.type}" already exists for this organization.`);
        }
        throw error;
      }

      // Process assign_bases if tenant_ids and tables_to_loaded are provided
      if (data && data.tenant_ids && data.tenant_ids.length > 0 && data.tables_to_loaded && Object.keys(data.tables_to_loaded).length > 0) {
        try {
          console.log('🔄 Processing assign_bases assignment...');
          const processResponse = await fetch(`/api/onboarding/assign-bases/${data.id}/process`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (!processResponse.ok) {
            const errorData = await processResponse.json();
            console.warn('⚠️  Failed to process assign_bases:', errorData.error);
            // Don't fail the entire operation, just show a warning
            toast({
              title: 'Assignment created',
              description: `Assignment created but processing failed: ${errorData.error}`,
              variant: 'destructive',
            });
          } else {
            const processResult = await processResponse.json();
            console.log('✅ Processed assign_bases:', processResult);
            toast({
              title: 'Success',
              description: `Assignment created and processed for ${processResult.processedTenants?.length || 0} tenant(s)`,
            });
          }
        } catch (processError: any) {
          console.error('Error processing assign_bases:', processError);
          // Don't fail the entire operation
          toast({
            title: 'Assignment created',
            description: 'Assignment created but processing encountered an error. Please try processing manually.',
            variant: 'destructive',
          });
        }
      } else {
        toast({
          title: 'Success',
          description: 'Assignment created successfully',
        });
      }

      setIsCreateDialogOpen(false);
      loadAssignments();
    } catch (error: any) {
      setDialogError(error.message || 'Failed to create assignment');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedAssignment) return;

    if (!formData.type.trim()) {
      setDialogError('Type is required');
      return;
    }

    setIsSaving(true);
    setDialogError(null);
    try {
      // Clean up tables_to_loaded: remove entries for bases not in bases_to_loaded
      const cleanedTablesToLoaded: TablesToLoaded = {};
      (formData.bases_to_loaded || []).forEach((baseName) => {
        if (formData.tables_to_loaded?.[baseName]) {
          cleanedTablesToLoaded[baseName] = formData.tables_to_loaded[baseName];
        }
      });

      const { data, error } = await supabase
        .schema('altzero')
        .from('assign_bases')
        .update({
          type: formData.type.trim(),
          bases_to_loaded: formData.bases_to_loaded || [],
          tables_to_loaded: Object.keys(cleanedTablesToLoaded).length > 0
            ? cleanedTablesToLoaded
            : null,
          tenant_ids: formData.tenant_ids && formData.tenant_ids.length > 0 ? formData.tenant_ids : null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedAssignment.id)
        .select()
        .single();

      if (error) {
        if (error.code === '23505') {
          throw new Error(`An assignment with type "${formData.type}" already exists for this organization.`);
        }
        throw error;
      }

      toast({
        title: 'Success',
        description: 'Assignment updated successfully',
      });

      setIsEditDialogOpen(false);
      loadAssignments();
    } catch (error: any) {
      setDialogError(error.message || 'Failed to update assignment');
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedAssignment) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .schema('altzero')
        .from('assign_bases')
        .delete()
        .eq('id', selectedAssignment.id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Assignment deleted successfully',
      });

      setIsDeleteDialogOpen(false);
      loadAssignments();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete assignment',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Assign Bases</h2>
          <p className="text-muted-foreground">
            Manage base assignments for different contexts
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadAssignments} variant="outline" disabled={isLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={handleCreate}>
            <Plus className="w-4 h-4 mr-2" />
            Create Assignment
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <TextField
              placeholder="Search assignments by type, bases, or organisation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search className="w-4 h-4" />
                  </InputAdornment>
                ),
              }}
              size="small"
              className="flex-1"
            />
            <div className="w-48">
              <Select
                value={selectedTenantFilter}
                onValueChange={(value) => setSelectedTenantFilter(value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Filter by Organisation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Assignments</SelectItem>
                  <SelectItem value="global">Global Only</SelectItem>
                  {spaces.map((space) => (
                    <SelectItem key={space.tenant_id} value={space.tenant_id}>
                      {space.tenant_name} {space.space_id ? `(Space: ${space.space_id})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results Count */}
      <div className="text-sm text-muted-foreground">
        Showing {filteredAssignments.length} of {assignments.length} assignments
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell>Bases to Load</TableCell>
                  <TableCell>Count</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" className="py-8">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                      <p className="text-muted-foreground">Loading assignments...</p>
                    </TableCell>
                  </TableRow>
                ) : filteredAssignments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" className="py-8">
                      <Box className="w-12 h-12 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-muted-foreground">No assignments found</p>
                      {searchQuery ? (
                        <p className="text-sm text-muted-foreground mt-1">
                          Try adjusting your search
                        </p>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAssignments.map((assignment) => (
                    <TableRow key={assignment.id} hover>
                      <TableCell>
                        <div className="font-medium">{assignment.type}</div>
                      </TableCell>
                      <TableCell>
                        {assignment.isGlobal ? (
                          <Chip
                            label="Global"
                            size="small"
                            color="default"
                            variant="outlined"
                          />
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {assignment.tenant_ids?.map((tenantId) => {
                              const space = spaces.find(s => s.tenant_id === tenantId);
                              return (
                                <Chip
                                  key={tenantId}
                                  label={space?.tenant_name || 'Unknown'}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              );
                            })}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {assignment.bases_to_loaded.length === 0 ? (
                            <span className="text-sm text-muted-foreground">No bases assigned</span>
                          ) : (
                            // Show only unique base names (filter out any table names that might be incorrectly stored)
                            (() => {
                              const uniqueBases = Array.from(new Set(assignment.bases_to_loaded));
                              // Filter to only show actual base names (check against availableBases)
                              const validBaseNames = uniqueBases.filter((baseName) =>
                                availableBases.some((baseGroup) => baseGroup.baseName === baseName)
                              );

                              // If no valid base names found, show all unique values (fallback for old data)
                              const basesToShow = validBaseNames.length > 0 ? validBaseNames : uniqueBases;

                              return basesToShow.map((base, index) => (
                                <Chip
                                  key={index}
                                  label={base}
                                  size="small"
                                  variant="outlined"
                                />
                              ));
                            })()
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={(() => {
                            const uniqueBases = Array.from(new Set(assignment.bases_to_loaded));
                            const validBaseNames = uniqueBases.filter((baseName) =>
                              availableBases.some((baseGroup) => baseGroup.baseName === baseName)
                            );
                            return validBaseNames.length > 0 ? validBaseNames.length : uniqueBases.length;
                          })()}
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleView(assignment)}
                            >
                              <Eye className="w-4 h-4" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(assignment)}
                            >
                              <Edit className="w-4 h-4" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(assignment)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={(open) => {
        setIsCreateDialogOpen(open);
        if (!open) setDialogError(null);
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Base Assignment</DialogTitle>
            <DialogDescription>
              Create a new base assignment configuration
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="type">Type *</Label>
              <Select
                value={formData.type}
                onValueChange={(value) =>
                  setFormData({ ...formData, type: value })
                }
              >
                <SelectTrigger id="type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="onboarding">Onboarding</SelectItem>
                  {/* Add more types here as needed in the future */}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="space-y-2">
                <Label htmlFor="tenant_ids">Organisations</Label>
                {isLoadingOrganizations ? (
                  <div className="flex items-center gap-2 py-2">
                    <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Loading spaces...</span>
                  </div>
                ) : (
                  <div className="border rounded-md p-2 max-h-48 overflow-y-auto space-y-2">
                    <div className="flex items-center space-x-2 pb-2 border-b mb-2">
                      <input
                        type="checkbox"
                        id="global-check"
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                        checked={!formData.tenant_ids || formData.tenant_ids.length === 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, tenant_ids: null });
                          } else {
                            // If unchecking global, don't select anything by default, user must select specific orgs
                            setFormData({ ...formData, tenant_ids: [] });
                          }
                        }}
                      />
                      <label
                        htmlFor="global-check"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        All Organisations (Global)
                      </label>
                    </div>

                    {spaces.map((space) => (
                      <div key={space.tenant_id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`org-${space.tenant_id}`}
                          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                          checked={formData.tenant_ids?.includes(space.tenant_id) || false}
                          disabled={formData.tenant_ids == null} // Disable if global is selected (null)
                          onChange={(e) => {
                            const currentIds = formData.tenant_ids || [];
                            let newIds: string[];
                            if (e.target.checked) {
                              newIds = [...currentIds, space.tenant_id];
                            } else {
                              newIds = currentIds.filter(id => id !== space.tenant_id);
                            }
                            setFormData({ ...formData, tenant_ids: newIds.length > 0 ? newIds : [] });
                          }}
                        />
                        <label
                          htmlFor={`org-${space.tenant_id}`}
                          className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                        >
                          {space.tenant_name} {space.space_id ? `(Space: ${space.space_id})` : ''}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Select specific organisations or check "All Organisations" for global assignment.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Bases and Tables to Load</Label>
                <span className="text-xs text-muted-foreground">
                  {formData.bases_to_loaded?.length || 0} bases selected
                </span>
              </div>

              {isLoadingBases ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading bases...</span>
                </div>
              ) : availableBases.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  No active bases available. Please create some base templates first.
                </div>
              ) : (
                <>
                  <TextField
                    placeholder="Search bases..."
                    value={baseSearchQuery}
                    onChange={(e) => setBaseSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search className="w-4 h-4" />
                        </InputAdornment>
                      ),
                    }}
                    size="small"
                    fullWidth
                    className="mb-3"
                  />

                  <div className="border rounded-lg p-3 max-h-96 overflow-y-auto bg-muted/30">
                    <div className="space-y-2">
                      {availableBases
                        .filter((baseGroup) =>
                          baseGroup.baseName.toLowerCase().includes(baseSearchQuery.toLowerCase()) ||
                          baseGroup.tables.some((table) =>
                            table.tableName.toLowerCase().includes(baseSearchQuery.toLowerCase()) ||
                            table.tablename.toLowerCase().includes(baseSearchQuery.toLowerCase())
                          )
                        )
                        .map((baseGroup) => {
                          const isBaseSelected = formData.bases_to_loaded?.includes(baseGroup.baseName) || false;
                          const isExpanded = expandedBases.has(baseGroup.baseName);

                          return (
                            <div key={baseGroup.baseName} className="border rounded-md">
                              {/* Base Row */}
                              <div
                                className="flex items-center gap-2 p-2 hover:bg-muted/50 cursor-pointer"
                                onClick={() => toggleBaseSelection(baseGroup.baseName)}
                              >
                                <div
                                  className="flex items-center gap-1 flex-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleBaseExpansion(baseGroup.baseName);
                                  }}
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                  )}
                                  <span className="font-medium">{baseGroup.baseName}</span>
                                  <span className="text-xs text-muted-foreground">
                                    ({baseGroup.tables.length} {baseGroup.tables.length === 1 ? 'table' : 'tables'})
                                  </span>
                                </div>
                                <Chip
                                  label={isBaseSelected ? 'Selected' : 'Not Selected'}
                                  size="small"
                                  color={isBaseSelected ? 'primary' : 'default'}
                                  variant={isBaseSelected ? 'filled' : 'outlined'}
                                  icon={isBaseSelected ? <CheckCircle className="w-3 h-3" /> : undefined}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleBaseSelection(baseGroup.baseName);
                                  }}
                                />
                              </div>

                              {/* Tables Row (when expanded and base is selected) */}
                              {isExpanded && isBaseSelected && (
                                <div className="pl-8 pr-2 pb-2 border-t bg-background/50">
                                  <div className="pt-2 space-y-1">
                                    {baseGroup.tables.map((table) => {
                                      const tableSelected = isTableSelected(baseGroup.baseName, table.tablename);
                                      return (
                                        <div
                                          key={`${baseGroup.baseName}-${table.tablename}`}
                                          className="flex items-center gap-2 p-1.5 hover:bg-muted/30 rounded cursor-pointer"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleTableSelection(baseGroup.baseName, table.tablename);
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={tableSelected}
                                            onChange={() => toggleTableSelection(baseGroup.baseName, table.tablename)}
                                            className="w-4 h-4"
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                          <span className="text-sm">{table.tableName}</span>
                                          {tableSelected && (
                                            <CheckCircle className="w-3 h-3 text-primary ml-auto" />
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      {availableBases.filter((baseGroup) =>
                        baseGroup.baseName.toLowerCase().includes(baseSearchQuery.toLowerCase()) ||
                        baseGroup.tables.some((table) =>
                          table.tableName.toLowerCase().includes(baseSearchQuery.toLowerCase()) ||
                          table.tablename.toLowerCase().includes(baseSearchQuery.toLowerCase())
                        )
                      ).length === 0 && (
                          <div className="text-sm text-muted-foreground py-4 w-full text-center">
                            No bases found matching "{baseSearchQuery}"
                          </div>
                        )}
                    </div>
                  </div>
                </>
              )}

              <p className="text-xs text-muted-foreground">
                Click on bases to select them, then expand to select specific tables. If no tables are selected, all tables in the base will be included.
              </p>
            </div>
          </div>

          {dialogError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{dialogError}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCreateDialogOpen(false)}
              disabled={isSaving}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSaveCreate} disabled={isSaving}>
              {isSaving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
        setIsEditDialogOpen(open);
        if (!open) setDialogError(null);
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Base Assignment</DialogTitle>
            <DialogDescription>
              Update base assignment: {selectedAssignment?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_name">Name *</Label>
              <Input
                id="edit_name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g., onboarding, default"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_tenant_ids">Organisations</Label>
              {isLoadingOrganizations ? (
                <div className="flex items-center gap-2 py-2">
                  <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Loading spaces...</span>
                </div>
              ) : (
                <div className="border rounded-md p-2 max-h-48 overflow-y-auto space-y-2">
                  <div className="flex items-center space-x-2 pb-2 border-b mb-2">
                    <input
                      type="checkbox"
                      id="edit-global-check"
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      checked={!formData.tenant_ids || formData.tenant_ids.length === 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({ ...formData, tenant_ids: null });
                        } else {
                          setFormData({ ...formData, tenant_ids: [] });
                        }
                      }}
                    />
                    <label
                      htmlFor="edit-global-check"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      All Organizations (Global)
                    </label>
                  </div>

                  {spaces.map((space) => (
                    <div key={space.tenant_id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`edit-org-${space.tenant_id}`}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                        checked={formData.tenant_ids?.includes(space.tenant_id) || false}
                        disabled={formData.tenant_ids == null} // Disable if global is selected (null)
                        onChange={(e) => {
                          const currentIds = formData.tenant_ids || [];
                          let newIds: string[];
                          if (e.target.checked) {
                            newIds = [...currentIds, space.tenant_id];
                          } else {
                            newIds = currentIds.filter(id => id !== space.tenant_id);
                          }
                          setFormData({ ...formData, tenant_ids: newIds.length > 0 ? newIds : [] });
                        }}
                      />
                      <label
                        htmlFor={`edit-org-${space.tenant_id}`}
                        className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {space.tenant_name} {space.space_id ? `(Space: ${space.space_id})` : ''}
                      </label>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Select specific organizations or check "All Organizations" for global assignment.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Bases and Tables to Load</Label>
                <span className="text-xs text-muted-foreground">
                  {formData.bases_to_loaded?.length || 0} bases selected
                </span>
              </div>

              {isLoadingBases ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-sm text-muted-foreground">Loading bases...</span>
                </div>
              ) : availableBases.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  No active bases available. Please create some base templates first.
                </div>
              ) : (
                <>
                  <TextField
                    placeholder="Search bases..."
                    value={baseSearchQuery}
                    onChange={(e) => setBaseSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search className="w-4 h-4" />
                        </InputAdornment>
                      ),
                    }}
                    size="small"
                    fullWidth
                    className="mb-3"
                  />

                  <div className="border rounded-lg p-3 max-h-96 overflow-y-auto bg-muted/30">
                    <div className="space-y-2">
                      {availableBases
                        .filter((baseGroup) =>
                          baseGroup.baseName.toLowerCase().includes(baseSearchQuery.toLowerCase()) ||
                          baseGroup.tables.some((table) =>
                            table.tableName.toLowerCase().includes(baseSearchQuery.toLowerCase()) ||
                            table.tablename.toLowerCase().includes(baseSearchQuery.toLowerCase())
                          )
                        )
                        .map((baseGroup) => {
                          const isBaseSelected = formData.bases_to_loaded?.includes(baseGroup.baseName) || false;
                          const isExpanded = expandedBases.has(baseGroup.baseName);

                          return (
                            <div key={baseGroup.baseName} className="border rounded-md">
                              {/* Base Row */}
                              <div
                                className="flex items-center gap-2 p-2 hover:bg-muted/50 cursor-pointer"
                                onClick={() => toggleBaseSelection(baseGroup.baseName)}
                              >
                                <div
                                  className="flex items-center gap-1 flex-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleBaseExpansion(baseGroup.baseName);
                                  }}
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                  )}
                                  <span className="font-medium">{baseGroup.baseName}</span>
                                  <span className="text-xs text-muted-foreground">
                                    ({baseGroup.tables.length} {baseGroup.tables.length === 1 ? 'table' : 'tables'})
                                  </span>
                                </div>
                                <Chip
                                  label={isBaseSelected ? 'Selected' : 'Not Selected'}
                                  size="small"
                                  color={isBaseSelected ? 'primary' : 'default'}
                                  variant={isBaseSelected ? 'filled' : 'outlined'}
                                  icon={isBaseSelected ? <CheckCircle className="w-3 h-3" /> : undefined}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleBaseSelection(baseGroup.baseName);
                                  }}
                                />
                              </div>

                              {/* Tables Row (when expanded and base is selected) */}
                              {isExpanded && isBaseSelected && (
                                <div className="pl-8 pr-2 pb-2 border-t bg-background/50">
                                  <div className="pt-2 space-y-1">
                                    {baseGroup.tables.map((table) => {
                                      const tableSelected = isTableSelected(baseGroup.baseName, table.tablename);
                                      return (
                                        <div
                                          key={`${baseGroup.baseName}-${table.tablename}`}
                                          className="flex items-center gap-2 p-1.5 hover:bg-muted/30 rounded cursor-pointer"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleTableSelection(baseGroup.baseName, table.tablename);
                                          }}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={tableSelected}
                                            onChange={() => toggleTableSelection(baseGroup.baseName, table.tablename)}
                                            className="w-4 h-4"
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                          <span className="text-sm">{table.tableName}</span>
                                          {tableSelected && (
                                            <CheckCircle className="w-3 h-3 text-primary ml-auto" />
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      {availableBases.filter((baseGroup) =>
                        baseGroup.baseName.toLowerCase().includes(baseSearchQuery.toLowerCase()) ||
                        baseGroup.tables.some((table) =>
                          table.tableName.toLowerCase().includes(baseSearchQuery.toLowerCase()) ||
                          table.tablename.toLowerCase().includes(baseSearchQuery.toLowerCase())
                        )
                      ).length === 0 && (
                          <div className="text-sm text-muted-foreground py-4 w-full text-center">
                            No bases found matching "{baseSearchQuery}"
                          </div>
                        )}
                    </div>
                  </div>
                </>
              )}

              <p className="text-xs text-muted-foreground">
                Click on bases to select them, then expand to select specific tables. If no tables are selected, all tables in the base will be included.
              </p>
            </div>
          </div>

          {dialogError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{dialogError}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
              disabled={isSaving}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={isSaving}>
              {isSaving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Base Assignment Details</DialogTitle>
            <DialogDescription>
              {selectedAssignment?.name}
            </DialogDescription>
          </DialogHeader>

          {selectedAssignment && (
            <div className="space-y-4">
              <div>
                <Label className="text-muted-foreground">Name</Label>
                <p className="font-medium">{selectedAssignment.name}</p>
              </div>

              <div>
                <Label className="text-muted-foreground">Bases to Load</Label>
                {selectedAssignment.bases_to_loaded.length === 0 ? (
                  <p className="text-muted-foreground">No bases assigned</p>
                ) : (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedAssignment.bases_to_loaded.map((base, index) => (
                      <Chip
                        key={index}
                        label={base}
                        size="small"
                        color="primary"
                      />
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label className="text-muted-foreground">Tables to Load</Label>
                {!selectedAssignment.tables_to_loaded || Object.keys(selectedAssignment.tables_to_loaded).length === 0 ? (
                  <p className="text-muted-foreground">All tables in selected bases will be included</p>
                ) : (
                  <div className="space-y-2 mt-2">
                    {Object.entries(selectedAssignment.tables_to_loaded).map(([baseName, tableNames]) => (
                      <div key={baseName} className="border rounded-md p-2">
                        <div className="font-medium text-sm mb-1">{baseName}</div>
                        <div className="flex flex-wrap gap-1 pl-4">
                          {tableNames.map((tableName, idx) => (
                            <Chip
                              key={idx}
                              label={tableName}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Created At</Label>
                  <p className="text-sm">
                    {new Date(selectedAssignment.created_at).toLocaleString()}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Updated At</Label>
                  <p className="text-sm">
                    {new Date(selectedAssignment.updated_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Assignment</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedAssignment?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isSaving}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              disabled={isSaving}
              variant="destructive"
            >
              {isSaving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div >
  );
};

export default AssignBasesPage;

